import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  faArrowDownShortWide,
  faArrowDownWideShort,
  faFilter,
  faRoute,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactDatePicker from 'react-datepicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import useGetRequest from '../../api/useGetRequest';
import usePostScheduleTours from '../../api/tours-planning/usePostScheduleTours';
import GenericPageContainer from '../../components/agv-generic-page-container';
import Datatable from '../../components/agv-datatable';
import SearchFilter from '../../components/agv-header-search';
import SelectFilter from '../../components/agv-header-select';
import CustomDatePickerDashboard from '../../components/agv-header-date-picker';
import AgrivitechPagination from '../../components/agv-pagination';
import MainButton from '../../components/agv-button';
import GenericModal from '../../components/agv-generic-modal';
import Status from '../../components/agv-status';
import { STATUS_ENUM } from '../../components/agv-status/status-enum';
import { ORDER_BY_ENUM } from '../../enums/orderby.enum';
import { buildUrlWithParams } from '../../utils/url.utils';
import { useSelector } from 'react-redux';
import './OrdersPool.css';

moment.locale('fr');

export default function OrdersPool() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [shouldFetchIds, setShouldFetchIds] = useState(false);
  const [idsHasBeenFetched, setIdsHasBeenFetched] = useState(false);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);

  const features = useSelector((state) => state.features);

  const [filters, setFilters] = useState({
    limit: 24,
    offset: searchParams.get('offset') || 0,
    query: searchParams.get('query') || '',
    order:
      searchParams.get('order') === ORDER_BY_ENUM.ASC ||
      searchParams.get('order') === ORDER_BY_ENUM.DESC
        ? searchParams.get('order')
        : ORDER_BY_ENUM.DESC,
    tour_date: searchParams.get('tour_date') || '',
    client_name: searchParams.get('client_name') || '',
  });

  /**
   * Handle the update of filters when a filter is changed
   * @param {string} key - The key of the filter
   * @param {string} value - The value of the filter
   * @returns {void}
   */
  const handleUpdateParam = useCallback(
    (key, value) => {
      setFilters((prevFilters) => {
        const newFilters = { ...prevFilters, [key]: value };

        if (key !== 'offset') {
          newFilters.offset = 0;
        }

        if (key === 'tour_date') {
          if (value) {
            newFilters[key] = value;
          } else {
            delete newFilters[key];
          }
        }

        if (
          (key === 'client_id' || key === 'client_name') &&
          (value === null || value === undefined || value === '')
        ) {
          delete newFilters[key];
        }

        return newFilters;
      });
    },
    [navigate]
  );

  const url = useMemo(
    () => buildUrlWithParams('/tours-planning', filters),
    [filters]
  );

  const idsUrl = useMemo(
    () =>
      shouldFetchIds
        ? buildUrlWithParams('/tours-planning/ids', {
            query: filters.query,
            client_name: filters.client_name,
            tour_date: filters.tour_date,
            order: filters.order,
          })
        : null,
    [
      shouldFetchIds,
      filters.query,
      filters.client_name,
      filters.tour_date,
      filters.order,
    ]
  );

  const { data, isLoading, isFirstLoad, mutate } = useGetRequest(url, 300);

  const { data: filtersData, isLoading: isLoadingFilters } = useGetRequest(
    '/tours-planning/filters',
    300
  );

  const { data: idsData, isLoading: isLoadingIds } = useGetRequest(idsUrl, 0);

  const {
    isLoading: isSchedulingLoading,
    isSuccess: isSchedulingSuccess,
    scheduleTours,
  } = usePostScheduleTours();

  /**
   * Update the URL with the new filters
   * @returns {void}
   */
  useEffect(() => {
    const params = new URLSearchParams(filters);
    navigate({ search: params.toString() }, { replace: true });
  }, [filters, navigate]);

  /**
   * Handle IDs data when it arrives
   */
  useEffect(() => {
    if (idsData && shouldFetchIds) {
      try {
        const { ids } = idsData;
        setSelectedItems(new Set(ids));
        toast.success(`${ids.length} étape(s) sélectionnée(s) au total`);
        setIdsHasBeenFetched(true);
      } catch (error) {
        console.error('Erreur lors de la récupération des IDs:', error);
        toast.error('Erreur lors de la sélection de toutes les étapes');
        setIdsHasBeenFetched(false);
      } finally {
        setIsSelectingAll(false);
        setShouldFetchIds(false);
      }
    }
  }, [idsData, shouldFetchIds]);

  /**
   * Calculate total weight and items count from delivery items
   * @param {array} items - Array of delivery items
   * @returns {object} - Object with total weight and items count
   */
  const calculateTotals = (items) => {
    if (!items || items.length === 0) return { totalWeight: 0, itemsCount: 0 };

    const totalWeight = items.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return sum + weight * quantity;
    }, 0);

    return {
      totalWeight: totalWeight.toFixed(2),
      itemsCount: items.length,
    };
  };

  /**
   * Get planning status
   * @param {object} element - The planning element
   * @returns {object} - Status object with enum and text
   */
  const getPlanningStatus = (element) => {
    switch (element?.status) {
      case 'PLANNED':
        return { status: STATUS_ENUM.WARN, text: 'Planifié' };
      case 'CREATED':
        return { status: STATUS_ENUM.NEUTRAL, text: 'Créé' };
      case 'IN_PROGRESS':
        return { status: STATUS_ENUM.POSITIVE, text: 'En cours' };
      case 'COMPLETED':
        return { status: STATUS_ENUM.POSITIVE, text: 'Terminé' };
      case 'CANCELLED':
        return { status: STATUS_ENUM.NEGATIVE, text: 'Annulé' };
      default:
        return { status: STATUS_ENUM.NEUTRAL, text: 'Non défini' };
    }
  };

  /**
   * Handle item selection
   * @param {string} itemId - The item ID to toggle
   */
  const handleItemSelection = (itemId) => {
    const newSelectedItems = new Set(selectedItems);
    if (newSelectedItems.has(itemId)) {
      newSelectedItems.delete(itemId);
    } else {
      newSelectedItems.add(itemId);
    }
    setSelectedItems(newSelectedItems);
    setIdsHasBeenFetched(false);
  };

  /**
   * Handle select all visible items
   */
  const handleSelectAllVisible = () => {
    const items = data?.items || [];
    if (selectedItems.size === items.length && items.length > 0) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((item) => item.id)));
    }
  };

  /**
   * Handle select all results (with API call to get all item IDs)
   */
  const handleSelectAllResults = () => {
    setIsSelectingAll(true);
    setShouldFetchIds(true);
  };

  /**
   * Check if all visible items are selected
   */
  const areAllVisibleSelected = () => {
    const items = data?.items || [];
    return items.length > 0 && selectedItems.size === items.length;
  };

  const needToShowSelectAllOption = () => {
    const items = data?.items || [];
    return (
      items.length > 0 &&
      selectedItems.size === items.length &&
      (selectedItems.size >= filters.limit || idsHasBeenFetched)
    );
  };

  /**
   * Get the appropriate message for "select all" based on current filters
   */
  const getSelectAllMessage = () => {
    const simulatedTotal = data?.pagination_info?.total_items;
    const hasFilters =
      filters.query || filters.client_name || filters.tour_date;

    if (hasFilters) {
      return `Sélectionner tous les ${simulatedTotal} résultats correspondant à cette recherche`;
    } else {
      return `Sélectionner toutes les ${simulatedTotal} étapes dans le puits de commande`;
    }
  };

  const onPlanningClick = (element) => {
    // TODO: Implémenter la logique d'ouverture des détails de planning
    console.log('Planning sélectionné:', element);
  };

  /**
   * Handle opening the date modal for scheduling
   */
  const handleOpenDateModal = () => {
    if (selectedItems.size === 0) return;
    setIsDateModalOpen(true);
  };

  /**
   * Handle sending selected steps to scheduling with selected date
   */
  const handleConfirmScheduling = async () => {
    if (selectedItems.size === 0 || !selectedDeliveryDate) return;
    const selectedIds = Array.from(selectedItems);
    const formattedDate = moment(selectedDeliveryDate).format('YYYY-MM-DD');
    await scheduleTours(selectedIds, formattedDate);
    setIsDateModalOpen(false);
    setSelectedDeliveryDate(null);
  };

  useEffect(() => {
    if (isSchedulingSuccess) {
      setSelectedItems(new Set());
      mutate();
    }
  }, [isSchedulingSuccess]);

  /**
   * Handle deleting a single item
   * @param {number} itemId - The item ID to delete
   */
  const handleDeleteItem = async (itemId, element) => {
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer l'étape ${element?.raw_data?.step?.end_address?.name || element?.data_key || itemId} ?`
      )
    ) {
      return;
    }

    try {
      // TODO: Implement API call to delete item
      console.log('Suppression étape:', itemId, element);

      toast.success(`Étape ${element?.raw_data?.step?.end_address?.name || element?.data_key || itemId} supprimée avec succès`);

      // Remove from selection if selected
      if (selectedItems.has(itemId)) {
        const newSelectedItems = new Set(selectedItems);
        newSelectedItems.delete(itemId);
        setSelectedItems(newSelectedItems);
      }

      // Refresh the list
      // mutate(); // Uncomment when API is implemented
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error("Erreur lors de la suppression de l'étape");
    }
  };

  return (
    <GenericPageContainer
      title={'Planification des tournées'}
      subtitle={'Gérez et organisez toutes les livraisons planifiées'}
      filters={
        <>
          <SearchFilter
            value={filters.query}
            setValue={(value) => handleUpdateParam('query', value)}
            placeholder="Rechercher par client, adresse, commentaire..."
          />
          <div className="tw-relative">
            <ReactDatePicker
              selected={filters.tour_date ? new Date(filters.tour_date) : null}
              onChange={(date) =>
                handleUpdateParam(
                  'tour_date',
                  date ? moment(date).format('YYYY-MM-DD') : ''
                )
              }
              customInput={<CustomDatePickerDashboard />}
              locale="fr"
              dateFormat="dd MMM yyyy"
              isClearable
              className="custom-datepicker"
              placeholderText="Sélectionner une date"
            />
          </div>
          <SelectFilter
            value={filters.client_name}
            onChange={(value) => handleUpdateParam('client_name', value)}
            options={[
              { value: '', name: 'Tous les clients' },
              ...(filtersData?.client_names || []).map((clientName) => ({
                value: clientName,
                name: clientName,
              })),
            ]}
            icon={faFilter}
            disabled={isLoadingFilters}
          />
          <SelectFilter
            value={filters.order}
            onChange={(value) => handleUpdateParam('order', value)}
            options={[
              { value: ORDER_BY_ENUM.DESC, name: 'Plus récent' },
              { value: ORDER_BY_ENUM.ASC, name: 'Plus ancien' },
            ]}
            icon={
              filters.order === ORDER_BY_ENUM.DESC
                ? faArrowDownWideShort
                : faArrowDownShortWide
            }
          />
        </>
      }
    >
      <button
        className={`btn btn-success tw-mr-2 ${selectedItems.size === 0 || isSchedulingLoading ? 'disabled' : ''}`}
        onClick={handleOpenDateModal}
        disabled={selectedItems.size === 0 || isLoading || isSchedulingLoading}
        title="Envoyer les étapes sélectionnées en ordonnancement"
        style={{
          opacity: selectedItems.size === 0 || isSchedulingLoading ? 0.6 : 1,
          cursor:
            selectedItems.size === 0 || isSchedulingLoading
              ? 'not-allowed'
              : 'pointer',
        }}
      >
        <FontAwesomeIcon icon={faRoute} className="me-1" />
        Envoyer en ordonnancement
        {selectedItems.size > 0 && (
          <span className="badge bg-light text-dark ms-2">
            {selectedItems.size} étape{selectedItems.size > 1 ? 's' : ''}
          </span>
        )}
      </button>
      {/* Gmail-style selection message */}
      {needToShowSelectAllOption() && (data?.items?.length || 0) > 0 && (
        <div
          className="selection-info mb-3 p-3 border rounded"
          style={{ backgroundColor: '#e3f2fd', borderColor: '#2196f3' }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <span style={{ color: '#1976d2', fontSize: '0.875rem' }}>
              Les <strong>{data?.items?.length || 0}</strong> étapes de cette page
              sont sélectionnées.
            </span>
            <button
              className="btn btn-link btn-sm p-0 mt-2 mt-md-0"
              style={{
                color: '#1976d2',
                textDecoration: 'underline',
                fontSize: '0.875rem',
              }}
              onClick={handleSelectAllResults}
              disabled={isSelectingAll}
            >
              {getSelectAllMessage()}
            </button>
          </div>
        </div>
      )}

      <Datatable
        style={'tw-mt-5'}
        isLoading={isLoading}
        isFirstLoading={isFirstLoad && isLoading}
        header={[
          <div
            key="selection-header"
            className="d-flex justify-content-center align-items-center"
          >
            <input
              className="form-check-input"
              type="checkbox"
              id="selectAllVisibleHeader"
              checked={areAllVisibleSelected()}
              onChange={handleSelectAllVisible}
              style={{ margin: '0' }}
            />
          </div>,
          'ETAPE / CLIENT',
          'VEHICULE',
          'ADRESSE',
          'DATE LIVRAISON',
          'STATUT',
          'ACTIONS',
        ]}
        data={data?.items}
        values={(element) => {
          const planningStatus = getPlanningStatus(element);
          const stepData = element?.raw_data?.step;
          const endAddress = stepData?.end_address;
          const deliveryDetails = element?.delivery_details || [];
          const allItems = deliveryDetails.flatMap((dd) => dd.items || []);
          const totals = calculateTotals(allItems);
          const blsCount = deliveryDetails.length;
          const vehicleImat = stepData?.vehicle_imat_if_any;

          return [
            <div
              key={`${element.id}_selection`}
              className="d-flex justify-content-center align-items-center"
            >
              <input
                className="form-check-input"
                type="checkbox"
                id={`planning-${element.id}`}
                checked={selectedItems.has(element.id)}
                onChange={() => handleItemSelection(element.id)}
                style={{ margin: '0' }}
              />
            </div>,
            <div key={`${element.id}_step_client`}>
              <div className="fw-bold" style={{ fontSize: '0.9rem' }}>
                {endAddress?.name || 'Client non défini'}
              </div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                {endAddress?.address_key || element?.data_key}
              </div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {blsCount > 1 && (
                  <Status
                    status={STATUS_ENUM.SECONDARY}
                    text={`${blsCount} BLs`}
                  />
                )}
                {totals.itemsCount > 1 && (
                  <Status
                    status={STATUS_ENUM.NEUTRAL}
                    text={`${totals.itemsCount} articles`}
                  />
                )}
              </div>
            </div>,
            <div key={`${element.id}_vehicle`}>
              <Status
                status={vehicleImat ? STATUS_ENUM.INFO : STATUS_ENUM.NEUTRAL}
                text={vehicleImat || 'Non assigné'}
              />
            </div>,
            <div key={`${element.id}_address`} style={{ maxWidth: '200px' }}>
              {endAddress ? (
                <div
                  className="text-truncate"
                  title={`${endAddress.street}, ${endAddress.city} ${endAddress.zipcode}`}
                >
                  <div className="fw-medium" style={{ fontSize: '0.9rem' }}>
                    {endAddress.street}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {endAddress.city} {endAddress.zipcode}
                  </div>
                </div>
              ) : (
                <span className="text-muted">Non définie</span>
              )}
            </div>,
            <div key={`${element.id}_date`}>
              {element?.delivery_date ? (
                <span className="fw-medium">
                  {new Date(element.delivery_date).toLocaleDateString('fr-FR')}
                </span>
              ) : (
                <span className="text-muted">Non définie</span>
              )}
            </div>,
            <div key={`${element.id}_status`}>
              <Status
                status={planningStatus.status}
                text={planningStatus.text}
              />
            </div>,
            <div key={`${element.id}_actions`} className="tw-flex tw-gap-2 tw-items-center">
              <MainButton
                onClick={() => onPlanningClick(element)}
                text="Détails"
                variant="secondary"
              />
              {features?.TOUR_PLANNING?.ORDERS_POOL?.canDeleteOrders && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDeleteItem(element.id, element)}
                  title="Supprimer cette étape"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>,
          ];
        }}
      />

      {/* Pagination */}
      <AgrivitechPagination
        data={data}
        setOffset={(value) => handleUpdateParam('offset', value)}
      />

      {/* Date Selection Modal */}
      <GenericModal
        isOpen={isDateModalOpen}
        setIsOpen={setIsDateModalOpen}
        title="Sélectionner la date de livraison"
        mainAction={handleConfirmScheduling}
        mainActionText="Confirmer"
        mainActionDisabled={!selectedDeliveryDate}
        mainActionIsLoading={isSchedulingLoading}
        secondaryAction={() => {
          setIsDateModalOpen(false);
          setSelectedDeliveryDate(null);
        }}
        secondaryActionText="Annuler"
      >
        <div className="tw-flex tw-flex-col tw-gap-4 tw-items-center">
          <p className="tw-text-sm tw-text-gray-600 tw-text-center">
            Veuillez sélectionner la date à laquelle les{' '}
            <strong>{selectedItems.size}</strong> étape{selectedItems.size > 1 ? 's' : ''} sélectionnée{selectedItems.size > 1 ? 's' : ''} doivent être livrées.
          </p>
          <div className="tw-flex tw-flex-col tw-gap-2 tw-items-center">
            <label className="tw-font-medium tw-text-sm">
              Date de livraison souhaitée
            </label>
            <ReactDatePicker
              selected={selectedDeliveryDate}
              onChange={(date) => setSelectedDeliveryDate(date)}
              locale="fr"
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              inline
            />
          </div>
          {selectedDeliveryDate && (
            <div className="tw-bg-blue-50 tw-border-2 tw-border-blue-200 tw-rounded-lg tw-p-4 tw-w-full">
              <p className="tw-text-sm tw-text-gray-700 tw-text-center">
                Date sélectionnée :{' '}
                <strong>
                  {moment(selectedDeliveryDate).format('dddd D MMMM YYYY')}
                </strong>
              </p>
            </div>
          )}
        </div>
      </GenericModal>
    </GenericPageContainer>
  );
}
